import { Controller } from "@hotwired/stimulus"
import RequestHelper from "../helpers/request_helper"

export default class extends Controller {
  static get targets() { return ["form", "colorButton", "labelColor"]; }
  static get classes() { return ["selected"]; }

  connect() {
    this.shouldReloadAfterModalExit = false;

    // shown.bs.modal/hidden.bs.modal are announced by the Bootstrap modal plugin through jQuery -
    // a native listener would never see them, so these two bindings stay jQuery.
    $(this.element).on("shown.bs.modal.contactLists", () => {
      this.shouldReloadAfterModalExit = false;
    });

    $(this.element).on("hidden.bs.modal.contactLists", () => {
      if (this.shouldReloadAfterModalExit) {
        // Native CustomEvent broadcast on `document` - contacts_controller.js listens the same
        // way, since `.content-box` isn't an ancestor of this modal (they're siblings) and
        // Stimulus's element-scoped dispatch() can't reach it.
        document.dispatchEvent(new CustomEvent("contact-lists-changed"));
      }
    });
  }

  disconnect() {
    $(this.element).off("shown.bs.modal.contactLists");
    $(this.element).off("hidden.bs.modal.contactLists");
  }

  pickColor(event) {
    event.preventDefault();
    const button = event.currentTarget;

    this.colorButtonTargets.forEach(colorButton => {
      colorButton.innerHTML = "";
      colorButton.classList.remove(this.selectedClass);
    });

    if (this.hasLabelColorTarget) {
      // R1 (skip): `data-color` is read off a target from a repeated collection
      // (colorButtonTargets), one color per button - the Values API is scoped to the controller
      // element and can't express a per-target attribute (D3).
      this.labelColorTarget.value = button.dataset.color;
    }

    // Static literal, no interpolation or user data - safe.
    button.innerHTML = "<i class='icon-lasso-check'></i>";
    button.classList.add(this.selectedClass);
  }

  visitInModal(event) {
    event.preventDefault();
    const url = event.currentTarget.closest("a").getAttribute("href");

    fetch(url, RequestHelper.defaultRequestOptions()).
      then(response => {
        if (!response.ok) { throw new Error(`Contact list lookup failed (${response.status})`); }
        return response.text();
      }).
      then(html => this.swapModalWithResponse(html)).
      catch(error => this.handleFailedCall(error));
  }

  deleteContactList(event) {
    event.preventDefault();
    const url = event.currentTarget.closest("a").getAttribute("href");

    fetch(url, RequestHelper.defaultDeleteOptions()).
      then(response => {
        if (!response.ok) { throw new Error(`Contact list delete failed (${response.status})`); }
        return response.text();
      }).
      then(html => this.swapModalWithResponse(html)).
      catch(error => this.handleFailedCall(error));
  }

  submitFormInModal(event) {
    event.preventDefault();
    const form = this.formTarget;
    const url = form.getAttribute("action");
    const contextField = form.querySelector("#context");
    const body = {
      contact_list: {
        name: form.querySelector("#contact_list_name").value,
        label_color: form.querySelector("#contact_list_label_color").value
      }
    };
    if (contextField) { body.context = contextField.value; }

    const isEdit = !!form.querySelector("input[name='_method']");
    const options = isEdit ? RequestHelper.defaultPutOptions() : RequestHelper.defaultPostOptions();
    options.headers = Object.assign({}, options.headers, { "Content-Type": "application/json" });
    options.body = JSON.stringify(body);

    fetch(url, options).
      then(response => {
        if (!response.ok) { throw new Error(`Contact list save failed (${response.status})`); }

        if (!isEdit && body.context === "contact_import" && response.status === 201) {
          window.location.reload();
          return null;
        }

        return response.text();
      }).
      then(html => { if (html !== null) { this.swapModalWithResponse(html); } }).
      catch(error => this.handleFailedCall(error));

    this.shouldReloadAfterModalExit = true;
  }

  // R2 (skip): `.modal-content` is rendered by three separate partials
  // (_form_modal/_index_modal/_assignments_index_modal.html.haml) and gets replaced wholesale via
  // outerHTML on every response - making it a target would mean adding the target attribute to
  // all three independently and re-verifying it re-registers after each full-element replacement.
  // Low value for one finding; left as a scoped query.
  swapModalWithResponse(html) {
    // S1: `html` is rendered server-side by Subdomain::ContactListsController's new/edit/index/
    // assignments_index actions (Haml, layout false) - same origin, and none of the three modal
    // partials use raw/html_safe/`!=`, so interpolated contact-list fields (name, label_color)
    // are escaped. Not user-supplied markup - safe to inject directly.
    this.element.querySelector(".modal-content").outerHTML = html;
  }

  handleFailedCall(error) {
    console.warn(error);
    Honeybadger.notify(error);
    window.ltAlert({ message: "An error occurred, please try again.", type: "toast-error" });
  }
}
